import { createAsyncThunk } from "@reduxjs/toolkit";
import apiInstance from "../../services/api";
import { login, logout, setIsGmail, setUser } from "../slices/userSlice";
import {
	googleSignIn,
	signIn,
	signOut,
	signUp,
} from "../../services/authService";
import { getUser } from "../../services/userService";
import { fantasyLogout } from "../slices/fantasySlice";

// 🔐 Authenticate on app load
export const authenticate = createAsyncThunk(
	"user/authenticate",
	async (_, thunkAPI) => {
		try {
			const res = await getUser();
			thunkAPI.dispatch(login(res.data));
			thunkAPI.dispatch(setUser(res.data));

			if (res.data.isGmail) {
				thunkAPI.dispatch(setIsGmail(true));
			} else {
				thunkAPI.dispatch(setIsGmail(false));
			}
			return res.data;
		} catch (err) {
			thunkAPI.dispatch(logout());
			return thunkAPI.rejectWithValue("Not authenticated");
		}
	},
);

// 🔑 Login + authenticate
export const loginThunk = createAsyncThunk(
	"user/loginThunk",
	async (credentials: { email: string; password: string }, thunkAPI) => {
		try {
			const res = await signIn(credentials);

			if (res) {
				localStorage.setItem("userId", res.data.userId);
				localStorage.setItem("role", res.data.role);
			}

			const user = await getUser();
			if (user) {
				thunkAPI.dispatch(login(user.data));
				thunkAPI.dispatch(setUser(user.data));
			}

			thunkAPI.dispatch(setIsGmail(false));

			return res.data;
		} catch (err) {
			thunkAPI.dispatch(logout());
			return thunkAPI.rejectWithValue("Login failed");
		}
	},
);

export const googleLoginThunk = createAsyncThunk(
	"user/googleLoginThunk",
	async (token: string, thunkAPI) => {
		try {
			const res = await googleSignIn({ token: token });

			if (res) {
				localStorage.setItem("userId", res.data.userId);
				localStorage.setItem("role", res.data.role);
			}

			const user = await getUser();
			if (user) {
				thunkAPI.dispatch(login(user.data));
				thunkAPI.dispatch(setUser(user.data));
			}

			thunkAPI.dispatch(setIsGmail(true));
			return res.data;
		} catch (err) {
			thunkAPI.dispatch(logout());

			return thunkAPI.rejectWithValue("Login failed");
		}
	},
);

export const signUpThunk = createAsyncThunk(
	"user/signUpThunk",
	async (
		credentials: {
			username: string;
			email: string;
			password: string;
			agreeToTerms: boolean;
			agreeToMarketing: boolean;
		},
		thunkAPI,
	) => {
		try {
			const res = await signUp(credentials);

			// Update Redux
			if (res) {
				localStorage.setItem("userId", res.data.userId);
				localStorage.setItem("role", res.data.role);
			}

			const user = await getUser();
			if (user) {
				thunkAPI.dispatch(login(user.data));
				thunkAPI.dispatch(setUser(user.data));
			}

			thunkAPI.dispatch(setIsGmail(false));
			return res.data;
		} catch (err) {
			thunkAPI.dispatch(logout());
			return thunkAPI.rejectWithValue("Sign-up failed");
		}
	},
);

export const logoutThunk = createAsyncThunk(
	"user/logoutThunk",
	async (_, thunkAPI) => {
		try {
			const res = await signOut();
			thunkAPI.dispatch(logout());
			thunkAPI.dispatch(fantasyLogout());

			localStorage.clear();
		} catch (err) {
			return thunkAPI.rejectWithValue("Logout failed");
		}
	},
);

export const getUserThunk = createAsyncThunk(
	"user/getUserThunk",
	async (_, thunkAPI) => {
		try {
			const res = await getUser();
			thunkAPI.dispatch(setUser(res.data));
			if (res.data.isGmail) {
				thunkAPI.dispatch(setIsGmail(true));
			} else {
				thunkAPI.dispatch(setIsGmail(false));
			}
			return res.data;
		} catch (err) {
			return thunkAPI.rejectWithValue("Get user failed");
		}
	},
);

export const updateUserThunk = createAsyncThunk(
	"user/updateUserThunk",
	async (userData: any, thunkAPI) => {
		try {
			const res = await apiInstance.put(`/users/${userData._id}`, userData);
			thunkAPI.dispatch(setUser(res.data));
			return res.data;
		} catch (err) {
			return thunkAPI.rejectWithValue("Update user failed");
		}
	},
);
